<template>
    <app-layout :title="$t('Edit workspace')">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                {{ $t('Edit workspace') }}
            </h2>
        </template>


        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <page-heading :breadcrumbs="breadcrumbs" class="px-4 sm:px-0">
                <template #title>
                    {{ customer.name }}
                    <Badge badge-type="warning" class="ml-2 gap-1" v-if="!customer.is_on_boarded">
                        <ClockIcon class="h-3.5 w-3.5" aria-hidden="true" />
                        {{ $t('OnBoarding pending') }}
                    </Badge>
                </template>
                <template #actionButtons>
                    <jet-secondary-button type="button" @click.prevent="downloadOnBoardingResult"
                                          v-if="hasOnBoardingData">
                        {{ $t('Export onboarding data') }}
                    </jet-secondary-button>
                </template>
            </page-heading>

            <div class="py-5">
                <div class="sm:hidden">
                    <label for="tabs" class="sr-only">{{ $t('Select tab') }}</label>
                    <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
                    <select id="tabs" name="tabs"
                            class="block w-full focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md"
                            v-model="selectedTab">
                        <option v-for="tab in filteredTabs" :key="tab.id" :value="tab.id">{{
                                $t(tab.name)
                            }}
                        </option>
                    </select>
                </div>
                <div class="hidden sm:block">
                    <nav class="relative z-0 rounded-lg shadow flex divide-x divide-gray-200" :aria-label="$t('Tabs')">
                        <a v-for="(tab, tabIdx) in filteredTabs" :key="tab.id" :href="tab.href"
                           :class="[selectedTab == tab.id ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700', tabIdx === 0 ? 'rounded-l-lg' : '', tabIdx === tabs.length - 1 ? 'rounded-r-lg' : '', 'group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10']"
                           :aria-current="selectedTab == tab.id ? 'page' : undefined" @click.prevent="changeTab(tab)">
                            <span>{{ $t(tab.name) }}</span>
                            <span aria-hidden="true"
                                  :class="[selectedTab == tab.id ? 'bg-primary-500' : 'bg-transparent', 'absolute inset-x-0 bottom-0 h-0.5']"/>
                        </a>
                    </nav>
                </div>
            </div>

            <div class="py-3">
                <template v-if="selectedTab == 'containers'">
                    <FileContainerContainer :containers="containers" :active-container="activeContainer"/>
                </template>

                <template v-if="selectedTab == 'master_data'">
                    <CustomerMasterDataManager :customer="customer"></CustomerMasterDataManager>
                </template>

                <template v-if="selectedTab == 'users'">
                    <UserManager :customer="customer"
                                 :available-users="availableUsers"
                    ></UserManager>
                </template>

                <template v-if="selectedTab == 'consultants'">
                    <ConsultantManager :customer="customer"
                                       :available-consultants="availableConsultants"></ConsultantManager>
                </template>
            </div>
        </div>
    </app-layout>
</template>

<script>
import {defineComponent} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import FileContainerContainer from '@/Components/FileContainerContainer.vue'
import ConsultantManager from '@/Components/ConsultantManager.vue'
import Badge from "@/Components/Badge";
import {ClockIcon} from '@heroicons/vue/outline'
import PageHeading from "@/Components/PageHeading";
import CustomerMasterDataManager from "@/Components/Customer/CustomerMasterDataManager";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import UserManager from "@/Components/UserManager.vue";

export default defineComponent({
    components: {
        AppLayout,
        FileContainerContainer,
        PageHeading,
        ConsultantManager,
        JetSecondaryButton,
        Badge,
        ClockIcon,
        CustomerMasterDataManager,
        UserManager,
    },

    computed: {
        filteredTabs() {
            return this.tabs.filter((tab) => !this.customer.is_consultant || tab.displayOnConsultant);
        }
    },

    props: {
        containers: Object,
        customer: Object,
        activeContainer: Object,
        availableConsultants: Object,
        availableUsers: Object,
        hasOnBoardingData: Boolean,
    },

    data() {
        return {
            tabs: [
                {name: 'File Storage', id: 'containers', href: '#', displayOnConsultant: true},
                {name: 'Master data', id: 'master_data', href: '#', displayOnConsultant: true},
                {name: 'Users', id: 'users', href: '#', displayOnConsultant: true},
                {name: 'Contacts', id: 'consultants', href: '#', displayOnConsultant: false},
            ],
            selectedTab: 'containers',
            breadcrumbs: [
                {
                    label: 'Workspaces',
                    url: route('customers.index'),
                },
                {
                    label: this.customer.name,
                    url: route('customers.show', {customer: this.customer.id}),
                }
            ]
        };
    },

    methods: {
        changeTab(tab) {
            this.selectedTab = tab.id;
        },

        downloadOnBoardingResult() {
            window.location.href = route('customer.on-boarding-results.show', {customer: this.customer.id});
        }
    }
})
</script>
